import React, { useState, useEffect, useRef } from "react";
import Button from "@mui/material/Button";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Box from "@mui/material/Box";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import {
  postDownloadZipPrepareAPI,
  getDownloadZipStatusAPI,
} from "../../../../helpers/call_api";
import { colors } from "../../../../components/theme";

// Poll every 2s (backend updates at most every ~2s or 5%); first poll runs immediately after job starts
const POLL_INTERVAL_MS = 2000;

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(3),
    minHeight: 160,
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
  },
}));

const DialogTitleWithClose = (props) => {
  const { children, onClose, ...other } = props;
  return (
    <DialogTitle sx={{ m: 0, p: 2, pb: 1 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

export function DownloadFolderZipModal({
  open,
  onClose,
  folderName = "folder",
  archiveIds = [],
}) {
  const [phase, setPhase] = useState("idle"); // idle | preparing | ready | failed
  const [errorMessage, setErrorMessage] = useState("");
  const [downloadUrl, setDownloadUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const [durationSeconds, setDurationSeconds] = useState(null);
  const pollTimeoutRef = useRef(null);

  const clearPolling = () => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  const poll = async (jobId) => {
    try {
      const result = await getDownloadZipStatusAPI(jobId);
      const job = result?.data ?? result;
      const status = job?.status;

      if (status === "Ready") {
        clearPolling();
        setDownloadUrl(job.downloadUrl || "");
        setFileName(job.fileName || `${folderName}.zip`);
        setDurationSeconds(
          job.durationSeconds != null ? Number(job.durationSeconds) : null
        );
        setPhase("ready");
        return;
      }
      if (status === "Failed") {
        clearPolling();
        setErrorMessage(job?.errorMessage || "Download failed.");
        setDurationSeconds(
          job.durationSeconds != null ? Number(job.durationSeconds) : null
        );
        setPhase("failed");
        return;
      }
      // Pending: update progress and schedule next poll
      const percent =
        job.progressPercent != null
          ? Math.min(100, Math.max(0, Number(job.progressPercent)))
          : undefined;
      if (percent !== undefined) setProgressPercent(percent);
      if (job.progressMessage != null)
        setProgressMessage(String(job.progressMessage));
      pollTimeoutRef.current = setTimeout(() => poll(jobId), POLL_INTERVAL_MS);
    } catch (err) {
      clearPolling();
      setErrorMessage(
        err?.response?.data?.error?.messages?.[0]?.message ||
          err?.message ||
          "Could not check download status."
      );
      setPhase("failed");
    }
  };

  useEffect(() => {
    if (!open || !archiveIds?.length) return;

    setPhase("preparing");
    setErrorMessage("");
    setDownloadUrl("");
    setFileName("");
    setProgressPercent(0);
    setProgressMessage("");
    setDurationSeconds(null);

    let cancelled = false;
    const ids = archiveIds;
    const name = folderName;

    const start = async () => {
      try {
        const result = await postDownloadZipPrepareAPI(name, ids);
        const jobId = result?.data?.jobId ?? result?.jobId;
        if (cancelled || !jobId) {
          if (!jobId) setErrorMessage("No job ID returned.");
          if (!jobId) setPhase("failed");
          return;
        }
        poll(jobId);
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(
          err?.response?.data?.error?.messages?.[0]?.message ||
            err?.message ||
            "Could not start download."
        );
        setPhase("failed");
      }
    };

    start();
    return () => {
      cancelled = true;
      clearPolling();
    };
    // Only start when modal opens; use props at open time
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) clearPolling();
  }, [open]);

  const handleClose = () => {
    clearPolling();
    onClose?.();
  };

  const handleDownload = () => {
    if (downloadUrl) {
      window.location.href = downloadUrl;
      handleClose();
    }
  };

  return (
    <StyledDialog
      onClose={handleClose}
      open={open}
      aria-labelledby="download-zip-modal-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitleWithClose id="download-zip-modal-title" onClose={handleClose}>
        Download folder as Zip
      </DialogTitleWithClose>
      <DialogContent>
        {phase === "preparing" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              justifyContent: "center",
              py: 2,
              gap: 2,
            }}
          >
            <Typography variant="body1" color="text.secondary">
              Preparing your download...
            </Typography>
            <LinearProgress
              variant={progressPercent > 0 ? "determinate" : "indeterminate"}
              value={progressPercent > 0 ? progressPercent : undefined}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: colors.gray3,
                "& .MuiLinearProgress-bar": {
                  backgroundColor: colors.blue,
                },
              }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center" }}
            >
              {progressMessage || "Starting…"}
            </Typography>
            {!progressMessage && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ textAlign: "center", mt: 0.5 }}>
                This may take a moment for large folders. You can keep using the app.
              </Typography>
            )}
          </Box>
        )}

        {phase === "ready" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 2,
              gap: 2,
            }}
          >
            <CheckCircleOutlineIcon
              sx={{ fontSize: 56, color: colors.green }}
            />
            <Typography variant="body1">Your download is ready.</Typography>
            {durationSeconds != null && !Number.isNaN(durationSeconds) && (
              <Typography variant="body2" color="text.secondary">
                Ready in {Number(durationSeconds).toFixed(1)} seconds
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {fileName}
            </Typography>
            <Button
              variant="contained"
              onClick={handleDownload}
              startIcon={<FolderZipIcon />}
              sx={{
                mt: 1,
                backgroundColor: colors.green,
                "&:hover": { backgroundColor: colors.greenHover },
              }}
            >
              Download
            </Button>
          </Box>
        )}

        {phase === "failed" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 2,
              gap: 2,
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 56, color: colors.red }} />
            <Typography variant="body1" color="error">
              Download could not be completed
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center" }}
            >
              {errorMessage}
            </Typography>
            {durationSeconds != null && !Number.isNaN(durationSeconds) && (
              <Typography variant="caption" color="text.secondary">
                Took {Number(durationSeconds).toFixed(1)} seconds before
                failing
              </Typography>
            )}
            <Button variant="outlined" onClick={handleClose} sx={{ mt: 1 }}>
              Close
            </Button>
          </Box>
        )}

        {phase === "idle" && open && archiveIds?.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={36} />
          </Box>
        )}
      </DialogContent>
    </StyledDialog>
  );
}

export default DownloadFolderZipModal;
